import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Globe,
  Landmark,
  RefreshCw,
  AlertCircle,
  Activity,
  Calendar
} from 'lucide-react';
import { LoadingSpinner } from 'components/common';
import api from '../../../../services/api';

function extrairBlocoGeracaoFinanceiro(dashboardData) {
  if (!Array.isArray(dashboardData)) return null;
  return (
    dashboardData.find(
      (item) =>
        item &&
        typeof item === 'object' &&
        (Object.prototype.hasOwnProperty.call(item, 'clientes_gerado_alguma_vez_sim') ||
          Object.prototype.hasOwnProperty.call(item, 'clientes_financeiro_gerado_no_mes'))
    ) || null
  );
}

function formatarValorCampo(raw) {
  if (raw === undefined || raw === null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : String(raw);
}

const ReelunetGeracaoFinanceiroDashboard = ({ action = 'reelunet-selected', refreshKey = 0 }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/automacoes/dashboard', {
        params: { action }
      });
      const payload = response?.data?.data ?? response.data;
      setDashboardData(payload);
    } catch (err) {
      console.error('Erro ao carregar Geração de Financeiro Reelunet:', err);
      setError(err.response?.data?.message || err.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [action]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData, refreshKey]);

  const bloco = useMemo(() => extrairBlocoGeracaoFinanceiro(dashboardData), [dashboardData]);

  const totalFinanceiroGerado = useMemo(
    () => formatarValorCampo(bloco?.clientes_gerado_alguma_vez_sim),
    [bloco]
  );

  const financeiroGeradoMensal = useMemo(
    () => formatarValorCampo(bloco?.clientes_financeiro_gerado_no_mes),
    [bloco]
  );

  if (loading) {
    return (
      <LoadingSpinner
        fullScreen={false}
        className="min-h-[240px] w-full"
        cardClassName="max-w-md w-full"
        label="Carregando Geração de Financeiro"
        subtitle="Aguarde enquanto atualizamos os dados."
      />
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-[color:var(--text-primary)] mb-2">Erro ao carregar dados</h3>
        <p className="text-[color:var(--text-muted)] mb-4">{error}</p>
        <button
          onClick={fetchDashboardData}
          className="inline-flex items-center px-4 py-2 bg-[color:var(--accent)] text-white rounded-md hover:opacity-90"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-[color:var(--surface)] rounded-lg shadow-sm border border-[color:var(--border)] p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
              <Globe className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[color:var(--text-primary)]">Reelunet</h1>
              <p className="text-[color:var(--text-muted)]">
                Dashboard de automações da Reelunet — Geração de Financeiro
              </p>
            </div>
          </div>
          <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg hidden sm:block">
            <Landmark className="h-6 w-6 text-[color:var(--accent)]" />
          </div>
        </div>
      </div>

      <div className="mb-8">
        <h3 className="text-lg font-semibold text-[color:var(--text-primary)] mb-4 flex items-center">
          <Activity className="h-5 w-5 mr-2 text-blue-600" />
          Geração de Financeiro
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-[color:var(--surface)] p-6 rounded-lg border border-[color:var(--border)]">
            <div className="flex items-center">
              <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                <Landmark className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4 min-w-0">
                <p className="text-sm font-medium text-[color:var(--text-muted)]">
                  Total financeiro gerado
                </p>
                <p className="text-2xl font-bold text-[color:var(--text-primary)] tabular-nums">
                  {totalFinanceiroGerado !== null ? totalFinanceiroGerado : '—'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-[color:var(--surface)] p-6 rounded-lg border border-[color:var(--border)]">
            <div className="flex items-center">
              <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                <Calendar className="h-6 w-6 text-emerald-600" />
              </div>
              <div className="ml-4 min-w-0">
                <p className="text-sm font-medium text-[color:var(--text-muted)]">
                  Financeiro gerado mensal
                </p>
                <p className="text-2xl font-bold text-[color:var(--text-primary)] tabular-nums">
                  {financeiroGeradoMensal !== null ? financeiroGeradoMensal : '—'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReelunetGeracaoFinanceiroDashboard;
